import React, { useState, useRef, useCallback } from "react";
import Webcam from "react-webcam";
import styles from "../../styles/shap_of_you.module.scss";
import { FaceScanningScreenProps } from "./types";
import { toast } from "react-toastify";

const FaceScanningScreen: React.FC<FaceScanningScreenProps> = ({
  navigateToStep,
  onCapture,
  logStep,
}) => {
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const webcamRef = useRef<Webcam>(null);

  // Camera ready callback
  const handleCameraReady = () => {
    setIsCameraReady(true);
  };

  // Handle camera error
  const handleCameraError = (error: string | DOMException) => {
    console.error("Camera error:", error);
    toast.error("Failed to access camera. Please check permissions.");
    navigateToStep("permissionDenied");
  };

  // Capture face from webcam
  const captureFace = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc && onCapture) {
        if (logStep) {
          logStep("face_scan_captured");
        }
        // Immediately freeze to captured image
        setCapturedImage(imageSrc);
        onCapture(imageSrc);
      } else {
        toast.error("Failed to capture image. Please try again.");
      }
    }
  }, [webcamRef, onCapture, logStep]);

  return (
    <div
      className="d-none d-md-block w-100"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "linear-gradient(180deg, #5e0000 0%, #2d0000 100%)",
        zIndex: 9999,
      }}
    >
      <div
        className={styles.wrapper_face}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          backgroundColor: "#000",
        }}
      >
        {/* Live Camera Feed */}
        <div
          className={styles.image}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#000",
          }}
        >
          {/* Show loader while camera is initializing */}
          {!isCameraReady && !capturedImage && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                zIndex: 10,
              }}
            >
              <div style={{ textAlign: "center", color: "#fff" }}>
                <div
                  className="spinner-border text-light mb-3"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}
                >
                  <span className="visually-hidden">Accessing camera...</span>
                </div>
                <p style={{ fontSize: "1rem", fontWeight: "bold" }}>
                  Accessing your camera...
                </p>
              </div>
            </div>
          )}

          {/* Webcam — hidden once image is captured */}
          <Webcam
            ref={webcamRef}
            audio={false}
            mirrored={true}
            screenshotFormat="image/jpeg"
            videoConstraints={{
              facingMode: "user",
              width: { ideal: 1280, min: 640 },
              height: { ideal: 720, min: 480 },
              aspectRatio: { ideal: 16 / 9 },
            }}
            onUserMedia={handleCameraReady}
            onUserMediaError={handleCameraError}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
              display: capturedImage ? "none" : "block",
            }}
          />

          {/* Frozen captured image — shown immediately on scan, stays while API loads */}
          {capturedImage && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={capturedImage}
              alt="Captured face"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center center",
                zIndex: 1,
              }}
            />
          )}

          {/* Face placement guide overlay - head & shoulders silhouette */}
          {isCameraReady && (
            <>
              {/* <svg
                width="100%"
                height="100%"
                viewBox="0 0 375 812"
                preserveAspectRatio="xMidYMid slice"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <defs>
                  <clipPath id="soy-silhouette-clip">
                    <path
                      d="
                      M 187.5 180
                      C 245 180, 280 230, 280 300
                      C 280 355, 255 395, 222 410
                      Q 218 422, 218 445
                      Q 218 475, 330 515
                      L 330 572
                      L 45 572
                      L 45 515
                      Q 157 475, 157 445
                      Q 157 422, 137 410
                      C 120 395, 95 355, 95 300
                      C 95 230, 130 180, 187.5 180
                      Z
                    "
                    />
                  </clipPath>
                </defs>

                <path
                  fillRule="evenodd"
                  fill="rgba(0, 0, 0, 0.85)"
                  d="
                    M 0 0 L 375 0 L 375 812 L 0 812 Z
                    M 187.5 180
                    C 245 180, 280 230, 280 300
                    C 280 355, 255 395, 222 410
                    Q 218 422, 218 445
                    Q 218 475, 330 515
                    L 330 572
                    L 45 572
                    L 45 515
                    Q 157 475, 157 445
                    Q 157 422, 137 410
                    C 120 395, 95 355, 95 300
                    C 95 230, 130 180, 187.5 180
                    Z
                  "
                />

                <path
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.9)"
                  strokeWidth="2.5"
                  strokeDasharray="12 10"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  d="
                    M 187.5 180
                    C 245 180, 280 230, 280 300
                    C 280 355, 255 395, 222 410
                    Q 218 422, 218 445
                    Q 218 475, 330 515
                    L 330 572
                    L 45 572
                    L 45 515
                    Q 157 475, 157 445
                    Q 157 422, 137 410
                    C 120 395, 95 355, 95 300
                    C 95 230, 130 180, 187.5 180
                    Z
                  "
                />
              </svg> */}
              <svg
                style={{
                  height: "100%",
                  width: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pointerEvents: "none",
                  zIndex: 2,
                }}
                viewBox="0 0 1920 1080"
                preserveAspectRatio="xMidYMid slice"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Full screen black overlay with silhouette cutout using fillRule evenodd */}
                <g transform="translate(760, 0) scale(1.2)">
                  <path
                    fillRule="evenodd"
                    fill="rgba(0, 0, 0, 0.75)"
                    d="M-633.333 0H966.667V900H-633.333V0ZM197 131C287.851 131 361.5 223.68 361.5 338.007C361.5 388.089 347.366 434.016 323.846 469.819H324C323.685 470.209 323.374 470.598 323.068 470.989C321.161 473.85 319.193 476.644 317.167 479.37C303.467 501.133 301.821 522.753 302.5 534.84C303.279 548.7 307.57 568.988 312.5 585.265C317.43 601.543 360.054 619.017 373 623.883V710H23V620.209C33.3333 613.131 57.4 596.235 71 585.265C88 571.553 92 550.322 92 523.34C92 509.129 85.9311 494.265 79.7871 483.244C50.5458 445.877 32.5 394.594 32.5 338.007C32.5 223.68 106.149 131 197 131Z"
                  />
                </g>

                {/* Dashed white outline of silhouette */}
                <path
                  transform="translate(760, 0) scale(1.2)"
                  d="M197 131C287.851 131 361.5 223.68 361.5 338.007C361.5 388.089 347.366 434.016 323.846 469.819H324C323.685 470.209 323.374 470.598 323.068 470.989C321.161 473.85 319.193 476.644 317.167 479.37C303.467 501.133 301.821 522.753 302.5 534.84C303.279 548.7 307.57 568.988 312.5 585.265C317.43 601.543 360.054 619.017 373 623.883V710H23V620.209C33.3333 613.131 57.4 596.235 71 585.265C88 571.553 92 550.322 92 523.34C92 509.129 85.9311 494.265 79.7871 483.244C50.5458 445.877 32.5 394.594 32.5 338.007C32.5 223.68 106.149 131 197 131Z"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="15 15"
                />
              </svg>
              {/* Instruction text at top */}
              <div
                style={{
                  position: "absolute",
                  top: "0px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#fff",
                  textAlign: "center",
                  fontSize: "15px",
                  zIndex: 4,
                  fontFamily: "Sweet Sans Pro Regular",
                  padding: "5px 30px",
                  borderRadius: "30px",
                  whiteSpace: "nowrap",
                  backgroundColor: "transparent",
                }}
              >
                Position your face in the outline
              </div>
              {/* Instruction text below the shape */}
              {/* <div
                style={{
                  position: "absolute",
                  top: "80%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#fff",
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "400",
                  zIndex: 4,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  maxWidth: "80%",
                  whiteSpace: "nowrap",
                }}
              >
                Keep neck visible for best results
              </div> */}
            </>
          )}
        </div>

        {/* Capture Button — hidden once scanning starts */}
        {isCameraReady && !capturedImage && (
          <div
            className={styles.button_cont_scan}
            style={{
              position: "absolute",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 5,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <button
              onClick={captureFace}
              style={{
                minWidth: "300px",
                background: "rgba(210, 35, 42, 1)",
                color: "#fff",
                border: "none",
                padding: "9px 40px",
                borderRadius: "50px",
                fontSize: "18px",
                fontFamily: "Sweet Sans Pro Medium",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0px",
                transition: "all 0.2s ease",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 3H5C3.89543 3 3 3.89543 3 5V7"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 3H19C20.1046 3 21 3.89543 21 5V7"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 9V11"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 9V11"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 15C9 15 10 16 12 16C14 16 15 15 15 15"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 21H5C3.89543 21 3 20.1046 3 19V17"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 21H19C20.1046 21 21 20.1046 21 19V17"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Scan Face</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaceScanningScreen;
